import { faFilePen, faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    AppBar,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    Divider,
    FormControlLabel,
    Grid,
    Paper,
    TextField,
    Toolbar,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useStyles } from '../../hooks/useStyles';
import { api } from '../../api';
import { SimpleAlertProps } from '../../components/SimpleAlert';
import { User } from '../../api/user';
import { MenuConfig } from '../../api/menu-config';
import { PageConfig } from '../../api/page-config';
import { EnumPage, EnumPageMap } from '../../data/PageMap';
import { useGlobal } from '../../units/global/GlobalProvider';
import { getMaxAuthority } from '../../api/user-group';
import { useAuth } from '../../units/auth/AuthProvider';

export const useEditUserGroupDialog = (props: { simpleAlert: SimpleAlertProps }) => {
    const { simpleAlert } = props;
    const classes = useStyles();
    const {
        userGroups,
        setUserGroups,
        reloadUserGroups,
        menuConfigs,
        setMenuConfigs,
        reloadMenuConfigs,
        pageConfigs,
        setPageConfigs,
        reloadPageConfigs,
    } = useGlobal();
    const auth = useAuth();
    const [open, setOpen] = useState(false);
    const [userGroupId, setUserGroupId] = useState('');
    const [inputId, setInputId] = useState('');
    const [inputDescr, setInputDescr] = useState('');
    const [inputAuthority, setInputAuthority] = useState<number>(0);
    const [originUsers, setOriginUsers] = useState<User[]>([]);
    const [originMenus, setOriginMenus] = useState<MenuConfig[]>([]);
    const [originPages, setOriginPages] = useState<PageConfig[]>([]);
    const [inputUsers, setInputUsers] = useState<User[]>([]);
    const [fixedUsers, setFixedUsers] = useState<User[]>([]);
    const [selectAllUsers, setSelectAllUsers] = useState<boolean>(false);
    const [inputMenus, setInputMenus] = useState<MenuConfig[]>([]);
    const [inputPages, setInputPages] = useState<PageConfig[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [menus, setMenus] = useState<MenuConfig[]>([]);
    const [pages, setPages] = useState<PageConfig[]>([]);
    const reloadUsers = () => {
        api.user
            .get({ relations: { userGroups: true } })
            .then(
                (ret) =>
                    ret.success &&
                    setUsers(
                        ret.data
                            ?.map((user) => ({ ...user, fixed: getMaxAuthority(user.userGroups) >= auth.profile.authority }))
                            .sort((a, b) => a.userName!.localeCompare(b.userName!)) || [],
                    ),
            );
    };
    const reloadMenus = () => {
        api.menuConfig
            .get({ relations: { userGroups: true } })
            .then(
                (ret) =>
                    ret.success &&
                    setMenus(
                        ret.data?.sort((a, b) => a.parentId!.localeCompare(b.parentId!)).sort((a, b) => a.id!.localeCompare(b.id!)) || [],
                    ),
            );
    };
    const reloadPages = () => {
        api.pageConfig.get({ relations: { userGroups: true } }).then((ret) => ret.success && setPages(ret.data || []));
    };
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    const onCreateUser = async () => {
        await api.userGroup
            .create({
                id: inputId || undefined,
                descr: inputDescr || undefined,
                authority: inputAuthority || undefined,
                users: inputUsers,
                menuConfigs: inputMenus,
                pageConfigs: inputPages,
            })
            .then((ret) => {
                if (ret.success) {
                    reloadUserGroups && reloadUserGroups();
                    setOpen(false);
                    simpleAlert?.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                } else {
                    simpleAlert?.show({ autoHideDuration: 6000, severity: 'error', message: JSON.stringify(ret) });
                }
            });
    };
    const onUpdateUser = async () => {
        await api.userGroup
            .update({
                userGroupId: userGroupId,
                id: inputId || undefined,
                descr: inputDescr || undefined,
                authority: inputAuthority || undefined,
                users: inputUsers,
                menuConfigs: inputMenus,
                pageConfigs: inputPages,
            })
            .then((ret) => {
                if (ret.success) {
                    reloadUserGroups && reloadUserGroups();
                    setOpen(false);
                    simpleAlert?.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                } else {
                    simpleAlert?.show({ autoHideDuration: 6000, severity: 'error', message: JSON.stringify(ret) });
                }
            });
    };
    const onReset = () => {
        setUserGroupId('');
        setInputId('');
        setInputDescr('');
        setInputAuthority(0);
        setOriginUsers([]);
        setInputUsers([]);
        setFixedUsers([]);
    };
    useEffect(() => {
        if (open) {
            reloadUsers();
            reloadMenus();
            reloadPages();
        } else {
            onReset();
        }
    }, [open]);
    useEffect(() => {
        if (userGroupId) {
            const userGroup = userGroups.filter((userGroup) => userGroup.userGroupId == userGroupId).find(() => true)!;
            setOriginUsers(userGroup.users || []);
            setOriginMenus(userGroup.menuConfigs || []);
            setOriginPages(userGroup.pageConfigs || []);
        }
    }, [userGroupId]);
    useEffect(() => {
        setInputUsers(users.filter((user) => originUsers.map((originUser) => originUser.userId).includes(user.userId!)));
        setFixedUsers(users.filter((user) => getMaxAuthority(user.userGroups) >= auth.profile.authority));
    }, [originUsers, users]);
    useEffect(() => {
        setInputMenus(menus.filter((menu) => originMenus.map((originMenu) => originMenu.menuConfigId).includes(menu.menuConfigId!)));
    }, [originMenus, menus]);
    useEffect(() => {
        setInputPages(pages.filter((page) => originPages.map((originPage) => originPage.pageConfigId).includes(page.pageConfigId!)));
    }, [originPages, pages]);
    return {
        classes,
        alert,
        open,
        setOpen,
        auth,
        userGroupId,
        setUserGroupId,
        inputId,
        setInputId,
        inputDescr,
        setInputDescr,
        inputAuthority,
        setInputAuthority,
        originUsers,
        setOriginUsers,
        originMenus,
        setOriginMenus,
        originPages,
        setOriginPages,
        inputUsers,
        setInputUsers,
        fixedUsers,
        setFixedUsers,
        selectAllUsers,
        setSelectAllUsers,
        inputMenus,
        setInputMenus,
        inputPages,
        setInputPages,
        users,
        setUsers,
        menus,
        setMenus,
        pages,
        setPages,
        onOpen,
        onClose,
        onCreateUser,
        onUpdateUser,
        onReset,
    };
};

export interface EditUserGroupDialogProps extends ReturnType<typeof useEditUserGroupDialog> {}

export const EditUserGroupDialog = (props: EditUserGroupDialogProps): React.ReactElement => {
    const {
        classes,
        alert,
        open,
        setOpen,
        auth,
        userGroupId,
        setUserGroupId,
        inputId,
        setInputId,
        inputDescr,
        setInputDescr,
        inputAuthority,
        setInputAuthority,
        originUsers,
        setOriginUsers,
        originMenus,
        setOriginMenus,
        originPages,
        setOriginPages,
        inputUsers,
        setInputUsers,
        fixedUsers,
        setFixedUsers,
        selectAllUsers,
        setSelectAllUsers,
        inputMenus,
        setInputMenus,
        inputPages,
        setInputPages,
        users,
        setUsers,
        menus,
        setMenus,
        pages,
        setPages,
        onOpen,
        onClose,
        onCreateUser,
        onUpdateUser,
        onReset,
    } = props;
    return (
        <Dialog fullWidth={true} maxWidth={'lg'} open={open} onClose={onClose}>
            <AppBar position='sticky' className={classes.appBar} color='primary'>
                <Toolbar>
                    <Typography style={{ width: '100%', textAlign: 'center' }} variant='h4' component={'div'}>
                        {userGroupId.length > 0 ? '更新群組' : '新增群組'}
                    </Typography>
                    <Typography style={{ position: 'absolute', right: '0px' }} variant='h4' component={'div'}>
                        <Button color='inherit' onClick={onClose}>
                            <Typography variant='h5'>
                                <FontAwesomeIcon icon={faXmark} size='lg' fixedWidth />
                            </Typography>
                        </Button>
                    </Typography>
                </Toolbar>
            </AppBar>
            <Grid container flexDirection={'column'} padding={4}>
                <Grid container item flexDirection={'row'}>
                    <Grid container item flexDirection={'row'} xs={12}>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <TextField
                                disabled={![auth.profile.authority >= inputAuthority].every((v) => v)}
                                value={inputId}
                                onChange={(e) => setInputId(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <span style={{ color: 'red' }}>*</span>
                                        {'群組ID'}
                                    </>
                                }
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <TextField
                                disabled={![auth.profile.authority >= inputAuthority].every((v) => v)}
                                value={inputDescr}
                                onChange={(e) => setInputDescr(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <span style={{ color: 'red' }}>*</span>
                                        {'群組描述'}
                                    </>
                                }
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12} md={6}>
                            <TextField
                                disabled={![auth.profile.authority > inputAuthority].every((v) => v)}
                                type={'number'}
                                value={Number(inputAuthority).toString()}
                                onChange={(e) => {
                                    const newValue = Number(e.target.value);
                                    newValue >= auth.profile.authority
                                        ? setInputAuthority(auth.profile.authority - 1)
                                        : setInputAuthority(newValue);
                                }}
                                variant='outlined'
                                label={
                                    <>
                                        <span style={{ color: 'red' }}>*</span>
                                        {'優先級'}
                                    </>
                                }
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12}>
                            <Autocomplete
                                disabled={![auth.profile.authority >= inputAuthority].every((v) => v)}
                                multiple
                                value={inputUsers}
                                onChange={(event, newValue) => setInputUsers(newValue)}
                                options={users}
                                getOptionLabel={(user) =>
                                    [
                                        user.userName ? `【名稱】${user.userName}` : user.account ? `【帳號】${user.account}` : undefined,
                                        user.mobile ? `【手機】${user.mobile}` : user.tel ? `【市話】${user.tel}` : undefined,
                                        user.email ? `【電子信箱】${user.email}` : undefined,
                                    ]
                                        .filter((v) => v)
                                        .join('、')
                                }
                                filterSelectedOptions
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='群組成員'
                                        placeholder={'搜尋使用者' + ['名稱', '帳號', '手機', '市話', '電子信箱'].join(', ')}
                                    />
                                )}
                                getOptionDisabled={(user) => fixedUsers.map((fixedUser) => fixedUser.account).includes(user.account)}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((user, index) => (
                                        <Chip
                                            label={[
                                                user.userName
                                                    ? `【名稱】${user.userName}`
                                                    : user.account
                                                    ? `【帳號】${user.account}`
                                                    : undefined,
                                                user.mobile ? `【手機】${user.mobile}` : user.tel ? `【市話】${user.tel}` : undefined,
                                                user.email ? `【電子信箱】${user.email}` : undefined,
                                            ]
                                                .filter((v) => v)
                                                .filter((v, i, a) => i < 2)
                                                .join('、')}
                                            {...getTagProps({ index })}
                                            disabled={fixedUsers.map((fixedUser) => fixedUser.account).includes(user.account)}
                                        />
                                    ))
                                }
                                PaperComponent={(paperProps) => {
                                    const { children, ...restPaperProps } = paperProps;
                                    return (
                                        <Paper {...restPaperProps}>
                                            <Box onMouseDown={(e) => e.preventDefault()} pl={1.5} py={0.5}>
                                                <FormControlLabel
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setSelectAllUsers(!selectAllUsers);
                                                        selectAllUsers
                                                            ? setInputUsers([...inputUsers.filter((user) => fixedUsers.includes(user))])
                                                            : setInputUsers([
                                                                  ...inputUsers,
                                                                  ...users
                                                                      .filter((user) => !inputUsers.includes(user))
                                                                      .filter((user) => !fixedUsers.includes(user)),
                                                              ]);
                                                    }}
                                                    label='全選'
                                                    control={<Checkbox checked={selectAllUsers} />}
                                                />
                                            </Box>
                                            <Divider />
                                            {children}
                                        </Paper>
                                    );
                                }}
                                disableClearable={true}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12}>
                            <Autocomplete
                                disabled={![auth.profile.authority >= inputAuthority].every((v) => v)}
                                multiple
                                value={inputPages}
                                onChange={(event, newValue) => setInputPages(newValue)}
                                options={pages}
                                getOptionLabel={(option) =>
                                    `${EnumPageMap[option.componentName as EnumPage]?.descr}【${
                                        EnumPageMap[option.componentName as EnumPage]?.key
                                    }】`
                                }
                                filterSelectedOptions
                                renderInput={(params) => <TextField {...params} label='可打開的頁面' placeholder='搜尋頁面名稱' />}
                                PaperComponent={(paperProps) => {
                                    const { children, ...restPaperProps } = paperProps;
                                    return (
                                        <Paper {...restPaperProps}>
                                            <Box onMouseDown={(e) => e.preventDefault()} pl={1.5} py={0.5}>
                                                <FormControlLabel
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        inputPages.length == pages.length ? setInputPages([]) : setInputPages(pages);
                                                    }}
                                                    label='全選'
                                                    control={
                                                        <Checkbox id='select-all-checkbox' checked={inputPages.length == pages.length} />
                                                    }
                                                />
                                            </Box>
                                            <Divider />
                                            {children}
                                        </Paper>
                                    );
                                }}
                                disableClearable={true}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} xs={12}>
                            <Autocomplete
                                disabled={![auth.profile.authority >= inputAuthority].every((v) => v)}
                                multiple
                                value={inputMenus}
                                onChange={(event, newValue) => setInputMenus(newValue)}
                                options={menus}
                                getOptionLabel={(option) => option.descr!}
                                filterSelectedOptions
                                renderInput={(params) => <TextField {...params} label='可看見的選單' placeholder='搜尋選單名稱' />}
                                PaperComponent={(paperProps) => {
                                    const { children, ...restPaperProps } = paperProps;
                                    return (
                                        <Paper {...restPaperProps}>
                                            <Box onMouseDown={(e) => e.preventDefault()} pl={1.5} py={0.5}>
                                                <FormControlLabel
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        inputMenus.length == menus.length ? setInputMenus([]) : setInputMenus(menus);
                                                    }}
                                                    label='全選'
                                                    control={
                                                        <Checkbox id='select-all-checkbox' checked={inputMenus.length == menus.length} />
                                                    }
                                                />
                                            </Box>
                                            <Divider />
                                            {children}
                                        </Paper>
                                    );
                                }}
                                disableClearable={true}
                                style={{ width: '100%', margin: 4, marginTop: 12 }}
                            />
                        </Grid>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            <Button
                                disabled={![inputId.length > 0, inputDescr.length > 0, inputAuthority > 0].every((v) => v)}
                                className={classes.buttonCyan}
                                style={{ margin: 4, marginTop: 12 }}
                                onClick={userGroupId.length > 0 ? onUpdateUser : onCreateUser}
                            >
                                <FontAwesomeIcon icon={userGroupId.length > 0 ? faFilePen : faPlus} fixedWidth />
                                {userGroupId.length > 0 ? '確定更新' : '確定新增'}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Dialog>
    );
};
